import { useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  Copy,
  Eye,
  EyeOff,
  Image as ImageIcon,
  Layers,
  Lock,
  Plus,
  Sparkles,
  Square,
  Trash2,
  Type,
  Unlock,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { duplicateLayer, newLayer, type BannerEditing, type BannerLayer } from '../model/bannerLayers';

const KIND_ICON: Record<BannerLayer['kind'], typeof Type> = {
  image: ImageIcon,
  text: Type,
  shape: Square,
  gradient: Sparkles,
  glass: Layers,
};

const ADD_KINDS: { kind: BannerLayer['kind']; label: string }[] = [
  { kind: 'image', label: 'Image' },
  { kind: 'text', label: 'Text' },
  { kind: 'shape', label: 'Shape' },
  { kind: 'gradient', label: 'Gradient' },
  { kind: 'glass', label: 'Glass' },
];

/**
 * Photoshop-style layer stack for Banner Studio: topmost first, with
 * visibility, lock, rename (double-click), duplicate, delete, and reorder.
 * Every operation writes through the profile editor's undo history.
 */
export function LayerPanel({ editing, onAddImage }: { editing: BannerEditing; onAddImage: () => void }) {
  const { layers, selectedId, onSelect: select, onSetLayers: setLayers, onPatchLayer: patchLayer } = editing;
  const [addOpen, setAddOpen] = useState(false);
  const [renaming, setRenaming] = useState<string | null>(null);

  // Panel shows top of the stack first (render order reversed).
  const stack = [...layers].reverse();

  function add(kind: BannerLayer['kind']): void {
    setAddOpen(false);
    if (kind === 'image') {
      // Image layers come from the asset library — open it; picking adds the layer.
      onAddImage();
      return;
    }
    const layer = newLayer(kind);
    setLayers([...layers, layer]);
    select(layer.id);
  }

  function move(id: string, dir: 1 | -1): void {
    // dir 1 = up in the panel = later in render order.
    const i = layers.findIndex((l) => l.id === id);
    const j = i + dir;
    if (i === -1 || j < 0 || j >= layers.length) return;
    const next = [...layers];
    const [a] = next.splice(i, 1);
    next.splice(j, 0, a!);
    setLayers(next);
  }

  function remove(id: string): void {
    setLayers(layers.filter((l) => l.id !== id));
    if (selectedId === id) select(null);
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="relative px-2 pb-1 pt-2">
        <button
          type="button"
          onClick={() => setAddOpen((v) => !v)}
          className="flex w-full items-center justify-center gap-1.5 rounded-[6px] border border-dashed border-white/15 py-1.5 text-[11.5px] font-bold text-white/50 transition-colors hover:border-white/30 hover:text-hi"
        >
          <Plus size={12} strokeWidth={2.6} /> Add layer
        </button>
        {addOpen && (
          <>
            <div className="fixed inset-0 z-30" onClick={() => setAddOpen(false)} />
            <div className="absolute inset-x-2 top-full z-40 mt-1 rounded-[8px] border border-white/[0.1] bg-ink-800 p-1 shadow-[0_20px_50px_rgba(0,0,0,0.6)]">
              {ADD_KINDS.map(({ kind, label }) => {
                const Icon = KIND_ICON[kind];
                return (
                  <button
                    key={kind}
                    type="button"
                    onClick={() => add(kind)}
                    className="flex w-full items-center gap-2 rounded-[5px] px-2 py-1.5 text-left text-[12px] font-semibold text-hi transition-colors hover:bg-white/[0.06]"
                  >
                    <Icon size={13} className="text-white/45" /> {label}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-2 pt-1">
        {stack.length === 0 && (
          <p className="px-2 py-3 text-[11.5px] leading-relaxed text-white/35">
            No layers yet — add an image, text, shape, gradient or glass panel to start designing.
          </p>
        )}
        {stack.map((layer) => {
          const Icon = KIND_ICON[layer.kind];
          const active = layer.id === selectedId;
          return (
            <div
              key={layer.id}
              onClick={() => select(layer.id)}
              onDoubleClick={() => setRenaming(layer.id)}
              className={cn(
                'group mb-0.5 flex h-8 cursor-pointer items-center gap-1.5 rounded-[6px] px-1.5 transition-colors',
                active ? 'bg-accent/[0.14]' : 'hover:bg-white/[0.05]',
                layer.hidden && 'opacity-45',
              )}
            >
              <Icon size={13} className={cn('flex-none', active ? 'text-accent' : 'text-white/40')} />
              {renaming === layer.id ? (
                <input
                  autoFocus
                  defaultValue={layer.name}
                  onBlur={(e) => {
                    patchLayer(layer.id, { name: e.target.value.trim() || layer.name });
                    setRenaming(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                    if (e.key === 'Escape') setRenaming(null);
                  }}
                  className="h-6 min-w-0 flex-1 rounded border border-accent/50 bg-black/40 px-1.5 text-[12px] font-semibold text-hi outline-none"
                />
              ) : (
                <span className={cn('min-w-0 flex-1 truncate text-[12px] font-semibold', active ? 'text-hi' : 'text-white/70')}>
                  {layer.name}
                </span>
              )}

              <span className="hidden flex-none items-center gap-0.5 group-hover:flex">
                <RowBtn label="Move up" onClick={(e) => { e.stopPropagation(); move(layer.id, 1); }}>
                  <ChevronUp size={12} />
                </RowBtn>
                <RowBtn label="Move down" onClick={(e) => { e.stopPropagation(); move(layer.id, -1); }}>
                  <ChevronDown size={12} />
                </RowBtn>
                <RowBtn label="Duplicate" onClick={(e) => { e.stopPropagation(); const d = duplicateLayer(layer); setLayers([...layers, d]); select(d.id); }}>
                  <Copy size={11} />
                </RowBtn>
                <RowBtn label="Delete" onClick={(e) => { e.stopPropagation(); remove(layer.id); }}>
                  <Trash2 size={11} />
                </RowBtn>
              </span>
              <RowBtn
                label={layer.locked ? 'Unlock' : 'Lock'}
                className={cn('flex-none', !layer.locked && 'opacity-0 group-hover:opacity-100')}
                onClick={(e) => { e.stopPropagation(); patchLayer(layer.id, { locked: !layer.locked }); }}
              >
                {layer.locked ? <Lock size={11} /> : <Unlock size={11} />}
              </RowBtn>
              <RowBtn
                label={layer.hidden ? 'Show' : 'Hide'}
                className="flex-none"
                onClick={(e) => { e.stopPropagation(); patchLayer(layer.id, { hidden: !layer.hidden }); }}
              >
                {layer.hidden ? <EyeOff size={12} /> : <Eye size={12} />}
              </RowBtn>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RowBtn({
  label,
  onClick,
  className,
  children,
}: {
  label: string;
  onClick: (e: React.MouseEvent) => void;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      className={cn('grid h-5 w-5 place-items-center rounded text-white/40 transition-colors hover:bg-white/10 hover:text-hi', className)}
    >
      {children}
    </button>
  );
}
